/**
 * viewReset.ts — put the viewport AND the keyboard back at the top of a screen
 * that has just been replaced.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: post-payment scroll reset
 * + thank-you route (2026-08-20).
 *
 * THE BUG THIS EXISTS TO PREVENT, in the CTO's own words: *"after payment it
 * looks like it goes to a blank page, but what happens is the page opens from
 * the bottom and you have to scroll to the top to get to the account creation
 * section."* Nothing anywhere in this app reset the scroll position — not on a
 * route change, not on a stage change. `CheckoutFlow` swaps a TALL payment
 * screen for a SHORT account screen inside one route, the document shrinks under
 * a window that is still scrolled a thousand pixels down, and the patient is
 * looking at empty space moments after handing over their card. It is the worst
 * possible moment on this product to look broken.
 *
 * WHY A SHARED HOOK AND NOT A FIX AT THE ONE REPORTED SCREEN. The defect is not
 * "the account screen"; it is "a screen was replaced and the window did not
 * move". That happens in two structurally different places, and neither can see
 * the other:
 *   • a ROUTE change — the app shell is the only component that wraps every
 *     route in BOTH mounting strategies (`App.tsx`'s classic `<Routes>` tree and
 *     `main.tsx`'s `createBrowserRouter`), so it is the one place a route-level
 *     reset can live once instead of N times;
 *   • a STAGE change INSIDE a route — `CheckoutFlow` and `JourneyPage` own stage
 *     machines that repaint the whole screen without touching the URL. The shell
 *     cannot see those at all; only the component holding the stage can.
 * One module, two call shapes, one definition of "reset the view" — so the day
 * the behaviour changes (focus target, scroll behaviour) it changes everywhere.
 *
 * WHY NOT react-router's own `<ScrollRestoration/>`: it is DATA-ROUTER ONLY. It
 * would work in production (`main.tsx`) and silently do nothing in `App.tsx` —
 * which is the tree every integration spec in this repo mounts. A fix that is
 * untestable and absent from half the app's mounting paths is not a fix.
 *
 * FOCUS MOVES TOO, AND THAT IS DELIBERATE. A sighted user gets the new screen
 * the moment we scroll; a screen-reader or keyboard user gets NOTHING from a
 * client-side screen swap — there is no page load to announce, and their focus
 * is still on a control that has just been removed from the DOM (browsers drop
 * that focus to `<body>`, so the next Tab restarts from the top of the whole
 * document, header and all). Moving focus to the top of the routed content
 * fixes both halves at once: assistive tech reads the new screen's `<h1>`, which
 * sits first inside that region, and the tab order restarts at the new content.
 *
 * WHY FOCUS AND NOT AN `aria-live` ANNOUNCEMENT: a live region would say
 * something and leave the keyboard stranded, which is the half of the problem
 * that actually blocks a person from continuing. It would also add a SECOND
 * assertive region to surfaces that are already required to keep exactly one
 * (see the R31 crisis-banner rule in CLAUDE.md).
 *
 * REDUCED MOTION COMES FROM `ui/motion`, never a second `matchMedia` call — one
 * signal for the whole estate, exactly as `safety/ClinicalSafetyPage.tsx` reads
 * it for its anchor scroll. `queryReducedMotion()` (the plain read) rather than
 * the hook: the answer is only needed at the instant we scroll, and subscribing
 * would let an OS setting change re-render and scroll the page from under
 * somebody mid-read.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { queryReducedMotion } from './motion';

/**
 * The DOM id the app shell puts on the element wrapping the routed content.
 *
 * WHY AN ID AND NOT A REF PASSED DOWN. The shell renders that wrapper inside one
 * of four route-family branches, so the element is REMOUNTED whenever a
 * navigation crosses families (journey → console). A ref captured by the
 * component that owns the branch would go stale on exactly the navigations this
 * hook exists to handle, and threading one ref through four branches plus two
 * unrelated stage machines is more moving parts than a single agreed id.
 */
export const ROUTED_CONTENT_ID = 'gps-routed-content';

/**
 * Scroll the window to the top of the page and hand focus to the routed content.
 *
 * Both calls are OPTIONAL (`?.`) on purpose, and the missing routed region is
 * handled rather than assumed: jsdom implements no layout, and the region does
 * not exist at all when a component is mounted outside the app shell (which is
 * every component-level spec in this repo). A missing scroll is a cosmetic loss;
 * a `TypeError` thrown from an effect on the post-payment screen would unmount
 * the tree and hand the patient the blank page we are here to remove.
 */
export function resetViewToTop(): void {
  // NO `typeof window` / `typeof document` GUARDS, deliberately — the same call
  // `lib/noIndex.ts` already makes in this repo. This only ever runs from an
  // effect, effects do not run during server rendering, and a guard for an
  // environment we never execute in is an untestable branch that reads as if it
  // were protecting something.
  window.scrollTo?.({ top: 0, left: 0, behavior: queryReducedMotion() ? 'auto' : 'smooth' });
  const region = document.getElementById(ROUTED_CONTENT_ID);
  if (!region) return;
  // `tabindex="-1"` makes a non-interactive container programmatically
  // focusable without putting it in the tab order. Set here rather than assumed,
  // so the reset still works if a future shell branch forgets the attribute.
  if (!region.hasAttribute('tabindex')) region.setAttribute('tabindex', '-1');
  // `preventScroll` so taking focus cannot undo the scroll we just asked for —
  // the browser's default is to scroll the focused element into view, which on a
  // tall screen would land somewhere other than the top.
  region.focus?.({ preventScroll: true });
}

/**
 * Reset the view whenever `key` changes — and NEVER on the first run.
 *
 * The first run is skipped because it is not a screen replacement: it is a fresh
 * mount, where the browser has already put the document at the top and assistive
 * tech has already announced the page. Stealing focus there would fight the
 * arrival, not help it.
 *
 * `skip` is honoured AFTER the key is recorded, so a change we deliberately sit
 * out cannot fire late on some unrelated re-render.
 */
function useResetView(key: unknown, skip: boolean): void {
  const previousKey = useRef(key);
  useEffect(() => {
    if (Object.is(previousKey.current, key)) return;
    previousKey.current = key;
    if (skip) return;
    resetViewToTop();
  }, [key, skip]);
}

/**
 * Reset the view when a stage machine repaints the screen without changing the
 * URL — `CheckoutFlow`'s plan/delivery/payment/account stages and `JourneyPage`'s
 * capture/questions/result/checkout ones.
 *
 * No fragment check here, and that is not an oversight: a stage change is not a
 * navigation to a place in the document, so a `#` left in the address bar has
 * nothing to say about it. (The journey's hand-off links genuinely arrive with
 * one — `#resume=`, `#lead=` — and honouring those here would silently disable
 * the reset for the patients arriving down a clinician's link.)
 */
export function useResetViewOnChange(key: unknown): void {
  useResetView(key, false);
}

/**
 * Reset the view on a route change — keyed on the PATHNAME only.
 *
 * NOT on the search string: the staff consoles and the patient portal drive
 * their sections with `?tab=` / `?section=`, and the journey stamps `?assessment=`
 * onto its own URL mid-flow. Resetting on those would yank a working screen to
 * the top (and move focus) for a change the user did not make.
 *
 * A FRAGMENT IS THE ONE THING THAT MUST WIN. `/weightloss-clinical-safety#treatment-x`
 * is a link a patient was SENT — from the payment screen, from a dispatch email,
 * from a clinician who pointed them at their own medicine — and that page's own
 * effect opens and scrolls to the section named in the hash. A blanket
 * scroll-to-top here would run AFTER it (parent effects fire after children's)
 * and drag the reader back to the top of a long page, off the paragraph they
 * followed the link to read. When the URL names a place in the document, this
 * hook has no business overriding it — so it does nothing at all.
 */
export function useResetViewOnRouteChange(): void {
  const { pathname, hash } = useLocation();
  useResetView(pathname, hash !== '');
}
